import { useCallback, useEffect, useRef } from 'react';

interface ReconnectStateMachineProps {
  maxAttempts: number;
  baseDelayMs: number;
  onReconnect: () => void;
  onExhausted: () => void;
}

export const useReconnectStateMachine = ({
  maxAttempts,
  baseDelayMs,
  onReconnect,
  onExhausted,
}: ReconnectStateMachineProps) => {
  const attemptsRef = useRef(0);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep dynamic references to callbacks and config to keep schedule()/reset()/clear() stable.
  const onReconnectRef = useRef(onReconnect);
  const onExhaustedRef = useRef(onExhausted);
  const maxAttemptsRef = useRef(maxAttempts);
  const baseDelayMsRef = useRef(baseDelayMs);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
    onExhaustedRef.current = onExhausted;
    maxAttemptsRef.current = maxAttempts;
    baseDelayMsRef.current = baseDelayMs;
  }, [onReconnect, onExhausted, maxAttempts, baseDelayMs]);

  const reset = useCallback(() => {
    attemptsRef.current = 0;
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    if (attemptsRef.current >= maxAttemptsRef.current) {
      onExhaustedRef.current();
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s...
    const delay = baseDelayMsRef.current * 2 ** attemptsRef.current;
    attemptsRef.current++;

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    timeoutIdRef.current = setTimeout(() => {
      onReconnectRef.current();
    }, delay);
  }, []);

  const clear = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const getAttempts = useCallback(() => attemptsRef.current, []);

  return {
    reset,
    schedule,
    clear,
    getAttempts,
  };
};
